import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import styles from "./styles/EmissionActivityModal.module.css";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";
import ShareIcon from "@mui/icons-material/Share";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import { flattenEAFormData } from "./form/utils/toRequestBody";
import { getEFSchemaByStandard } from "./utils/getEFSchemaByStandard";
import { emissionFactorAPI } from "../API/emissionFactorAPI";
import emissionViewStore from "../../Store/marco/emissionViewStore";
import commonStore from "../../Store/marco/commonStore";
import { emissionFactorManagementAPI } from "../../EmissionFactorManagement/API/emissionFactorManagementAPI";
import ViewCustomEFAttachModal from "./EmissionActivity/ViewCustomEFAttachModal";
import { normalizeCustomEFPermissions } from "../../EmissionFactorManagement/services/customEFSharing";
const specialCats = ["4_3"];

export const EFDec = observer(({ parentData, EFFormData, target }) => {
  const { t } = useTranslation("ActivityMain");
  const { t: t1 } = useTranslation("EFMain");
  const store = emissionViewStore;
  const { EFDec } = emissionFactorAPI;
  ////////category轉換
  const schema = getEFSchemaByStandard(store.standardId)[store.selectedTab];

  ////3.1 3.2
  const category = schema?.cat;

  const isGHGThreeFourteen =
    store.standardId === 2 &&
    store.selectedTab === "3.14 Franchise (Scope 1 and Scope 2)";

  ////cat1 cat2 cat3
  const cat = isGHGThreeFourteen ? "cat5" : schema?.catName;

  const [EFDecData, setEFDecData] = useState([]);

  // 佐證資料 Modal state
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [validationFiles, setValidationFiles] = useState([]);

  const EAData = flattenEAFormData(store.EAFormData);
  const { modeId } = EAData;

  let data = flattenEAFormData(EFFormData);
  const customEFPermissions = normalizeCustomEFPermissions(
    EFDecData?.id ? EFDecData : data,
  );

  // 是否為自定義係數（emissionFactorTypeId === "2"）
  const isCustomEF = String(data?.emissionFactorTypeId) === "2";
  const customEFId = data?.emissionFactorId;

  // 公司 id（用於新增/刪除佐證檔案用）
  const companyId =
    store?.selectedDataEntry?.companyId || EAData?.companyId || "";

  // 開啟佐證資料 Modal：直接儲存伺服器物件，不下載 blob
  const handleOpenAttach = async () => {
    if (!customEFId) return;
    try {
      const fileList =
        await emissionFactorManagementAPI.getValidationFileAPI(customEFId);
      setValidationFiles(fileList || []);
    } catch (e) {
      console.error("Error fetching validation files", e);
      setValidationFiles([]);
    }
    setAttachModalOpen(true);
  };

  // 關閉 Modal：如果有做修改則重新擈取
  const handleCloseAttach = async (needRefresh) => {
    setAttachModalOpen(false);
    if (needRefresh && customEFId) {
      try {
        const fileList =
          await emissionFactorManagementAPI.getValidationFileAPI(customEFId);
        setValidationFiles(fileList || []);
      } catch (e) {
        console.error("Error re-fetching validation files", e);
      }
    }
  };

  ////////拿取EF詳細資料
  const getEFDescription = async (EFId, EFTId) => {
    if (parentData) {
      return setEFDecData(parentData[0]);
    }

    try {
      let data = [];

      data = await EFDec(cat, EFTId, EFId);

      setEFDecData(data);
    } catch (error) {
      console.error("Error fetching EF詳細資料", error);
      setEFDecData([]);
    }
  };

  useEffect(() => {
    const hasValid = (v) => Number(v) > 0;
    const tryCall = (id, type) => {
      if (hasValid(id) && hasValid(type)) {
        getEFDescription(Number(id), Number(type));
        return true;
      }
      return false;
    };

    // ---------- 1️⃣ 沒有任何有效 EFId 時清空 ----------
    const noValidEF =
      !hasValid(data.emissionFactorId) &&
      !hasValid(data.secondEFId) &&
      !hasValid(data.landEFId);
    if (noValidEF) {
      setEFDecData([]);
      return; // ✅ 直接結束
    }

    // ---------- 2️⃣ 一般類別 ----------
    if (!specialCats.includes(category)) {
      if (tryCall(data.emissionFactorId, data.emissionFactorTypeId)) return;
      setEFDecData([]);
      return;
    }

    // ---------- 3️⃣ 特殊類別 ----------
    let called = false;

    switch (category) {
      /** 🚚 類別 4_3（複合型：EFFormData + EFLandFormData） */
      case "4_3":
        if (target === "EFFormData") {
          called ||= tryCall(data.emissionFactorId, data.emissionFactorTypeId);
        } else if (target === "EFLandFormData") {
          called ||= tryCall(data.landEFId, data.landEFTypeId);
        }
        break;

      /** 🚗 一般 land 類別 */
      default:
        if (target === "EFLandFormData") {
          called ||= tryCall(data.emissionFactorId, data.emissionTypeId);
        }

        /** 🚢✈️ Sea / Air 類別 */
        if (["EFSeaFormData", "EFAirFormData"].includes(target)) {
          called ||= tryCall(data.secondEFId, data.secondEmissionTypeId);
        }

        /** 🌀 未指定 modeId（fallback） */
        if (!modeId) {
          called ||= tryCall(data.emissionFactorId, data.emissionTypeId);
        }
        break;
    }

    // ---------- 4️⃣ 若三種都沒命中 ----------
    if (!called) setEFDecData([]);
  }, [EFFormData]);

  if (EFDecData?.length === 0 || EFDecData === null) return;

  const getGWP = (EFDecData, EAData) => {
    const list = [
      { label: "CO₂", value: EFDecData?.co2GWPValueExternal },
      {
        label: "CH₄",
        value: EAData?.isFossil
          ? EFDecData?.ch4FGWPValueExternal
          : EFDecData?.ch4NoGWPValueExternal,
      },
      { label: "N₂O", value: EFDecData?.n2oGWPValueExternal },
      { label: "HFCs", value: EFDecData?.hfcsGWPValueExternal },
      { label: "PFCs", value: EFDecData?.pfcsGWPValueExternal },
      { label: "SF₆", value: EFDecData?.sf6GWPValueExternal },
      { label: "NF₃", value: EFDecData?.nf3GWPValueExternal },
    ];

    // 過濾掉 <= 0、null、undefined 的
    return list.filter((item) => Number(item.value) > 0);
  };

  const gwpList = getGWP(EFDecData, EAData);
  const customEFYear = EFDecData?.year ?? data?.year;
  const customEFIsBiomass = EFDecData?.isBiomass ?? data?.isBiomass;
  const hasCustomEFIsBiomass =
    customEFIsBiomass !== undefined && customEFIsBiomass !== null;
  const isCustomEFBiomass =
    customEFIsBiomass === true ||
    customEFIsBiomass === 1 ||
    customEFIsBiomass === "1" ||
    customEFIsBiomass === "true";

  return (
    <div className={styles.EFDec}>
      <div className={styles.EFDecEFTitle}>{t("Emission Factor Value")}</div>
      {(() => {
        const values = [
          {
            label: "CO₂",
            value: EFDecData?.co2EFValue,
          },
          {
            label: "CH₄",
            value: EFDecData?.ch4EFValue,
          },
          {
            label: "N₂O",
            value: EFDecData?.n2oEFValue,
          },
          {
            label: "HFCs",
            value: EFDecData?.hfcsEFValue,
          },
          {
            label: "PFCs",
            value: EFDecData?.pfcsEFValue,
          },
          {
            label: "SF₆",
            value: EFDecData?.sf6EFValue,
          },
          {
            label: "NF₃",
            value: EFDecData?.nf3EFValue,
          },
          {
            label: "CO₂e",
            value: EFDecData?.co2eEFValue,
          },
        ].filter(
          (item) =>
            item.value !== undefined && item.value !== null && item.value > 0,
        );

        return (
          <main className={styles.EFDecMain}>
            {values.map((value, index) => (
              <div className={styles.EFDecInputWrap}>
                #{index + 1}：{value.value}{" "}
                {index === 0
                  ? EFDecData?.firstUnit
                  : index === 1
                    ? EFDecData?.secondUnit
                    : EFDecData?.thirdUnit}
              </div>
            ))}
          </main>
        );
      })()}

      {category === "1_4" &&
        EFDecData?.emissionTypeId === 1 &&
        gwpList.length > 0 && (
          <>
            <div className={styles.EFDecGWPTitle}>{"GWP"}</div>
            <div className={styles.GWPWrap}>
              {gwpList.map((gwp) => (
                <p key={gwp.label}>
                  {gwp.label}：{gwp.value}
                </p>
              ))}
            </div>
          </>
        )}

      <div className={styles.EFDecline}></div>
      <footer>
        <p className={styles.EFDecSmallTitle}>
          {EFDecData?.descEF ? (
            t("Source Description") + "：" + t1(EFDecData.descEF)
          ) : (
            <></>
          )}
        </p>
        <p className={styles.EFDecSmallTitle}>
          {EFDecData?.databaseName ? (
            t("Official Source Name") + "：" + t1(EFDecData?.databaseName)
          ) : (
            <></>
          )}
        </p>
        <p className={styles.EFDecSmallTitle}>
          {EFDecData?.databaseDesc ? (
            t("Description") + "：" + t1(EFDecData?.databaseDesc)
          ) : (
            <></>
          )}
        </p>
        <p className={styles.EFDecSmallTitle}>
          {EFDecData?.databaseUpdD ? (
            t("Official Update Date") + "：" + EFDecData?.databaseUpdD
          ) : (
            <></>
          )}
        </p>
        <p className={styles.EFDecSmallTitle}>
          {EFDecData?.databaseURL ? (
            <>
              {t("Official Link")}：
              <a
                href={EFDecData?.databaseURL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ShareIcon style={{ color: "rgb(15, 110, 250)" }} />
              </a>
            </>
          ) : null}
        </p>
        {(isCustomEF || EFDecData?.fileDownloadLink) && (
          <p className={styles.EFDecSmallTitle}>
            {t("Platform File Download")}：
            <a
              href={!isCustomEF ? EFDecData.fileDownloadLink : undefined}
              onClick={isCustomEF ? handleOpenAttach : undefined}
              style={{ cursor: "pointer" }}
            >
              <SimCardDownloadIcon style={{ color: "rgb(15, 110, 250)" }} />
            </a>
          </p>
        )}
        <p className={styles.EFDecSmallTitle}>
          {EFDecData?.fileName ? (
            t("File Name") + "：" + t1(EFDecData?.fileName)
          ) : (
            <></>
          )}
        </p>
        <p className={styles.EFDecSmallTitle}>
          {EFDecData?.fileDesc ? (
            t("Remarks") + "：" + t1(EFDecData?.fileDesc)
          ) : (
            <></>
          )}
        </p>
        {isCustomEF && hasCustomEFIsBiomass && (
          <p className={styles.EFDecSmallTitle}>
            {t("Biomass")}：{isCustomEFBiomass ? t("Yes") : t("No")}
          </p>
        )}
        {isCustomEF && customEFYear && (
          <p className={styles.EFDecSmallTitle}>
            {t("Reference Year")}：{customEFYear}
          </p>
        )}
        {/* 熱值資訊 */}
        {EFDecData?.calorificValue !== undefined &&
          EFDecData?.calorificValue !== null && (
            <p className={styles.EFDecSmallTitle}>
              {t("Calorific Value")}：{EFDecData.calorificValue}{" "}
              {EFDecData.calorificValueUnit || ""}
            </p>
          )}
        {EFDecData?.calorificValueSourceDesc && (
          <p className={styles.EFDecSmallTitle}>
            {t("Calorific Value Source")}：
            {t1(EFDecData.calorificValueSourceDesc)}
          </p>
        )}
      </footer>

      {/* 自定義係數佐證資料 Modal */}
      <ViewCustomEFAttachModal
        open={attachModalOpen}
        onClose={handleCloseAttach}
        fileList={validationFiles}
        efName={EFDecData?.nameEF || ""}
        customEFId={customEFId}
        companyId={companyId}
        readOnly={
          Number(commonStore.roleId) !== 2 &&
          !customEFPermissions.canManageEvidence
        }
        creatorName={customEFPermissions.creatorName}
      />
    </div>
  );
});
